"use client";

import { useState, useLayoutEffect } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { getPageFeedAPI } from "../../api/homeApi";
import { LAST_VISITED_PINCODE } from "../../constants/storage";
import { setDesktopFeed, setMobileStoreCampaignFeed } from "../../store/slices/dynamicHomeSlice";
import CookieUtil from "../../utils/storage/cookieUtil";
import {
  extractCampaignWidgetsFromFeedResponse,
  getInitialMobileStore,
  mergeCampaignWidgetsById,
} from "../../utils/dynamicHomeUtils/mergeCampaignWidgetsById";

const CAMPAIGN_LOADER_DISMISS_MS = 100;

/**
 * Fetches personalized campaign widget slots after paint and merges them into the cacheable SSR base feed.
 * Desktop updates `desktopWidgets`; mobile updates the selected store in `pageStores`.
 * Campaign overlay is always dismissed after {@link CAMPAIGN_LOADER_DISMISS_MS}, independent of API timing.
 *
 * @param {Object} args
 * @param {boolean} args.isMobile - Whether the current device is mobile (msite).
 * @param {object|null|undefined} args.serverPageData - SSR feed: `{ widgets }` (desktop) or `{ stores }` (mobile).
 * @param {string} [args.userUUID] - Anonymous / experiment user id from SSR.
 * @returns {boolean} True while the campaign overlay loader should be visible.
 */
const useHomeCampaignFeed = ({ isMobile, serverPageData, userUUID }) => {
  const dispatch = useDispatch();
  const [isCampaignLoading, setIsCampaignLoading] = useState(true);

  const emailID = useSelector((state) => state?.loginSignUp?.userDetails?.emailID, shallowEqual);

  useLayoutEffect(() => {
    let cancelled = false;
    setIsCampaignLoading(true);

    // Hard fallback: always clear the overlay after this delay, even if the API hangs or base feed is missing.
    const dismissTimer = setTimeout(() => {
      if (!cancelled) {
        setIsCampaignLoading(false);
      }
    }, CAMPAIGN_LOADER_DISMISS_MS);

    /**
     * Loads campaign-only widgets and merges them into the SSR base feed in Redux.
     * @returns {Promise<void>}
     */
    const fetchCampaignFeed = async () => {
      try {
        const fnpId = userUUID || CookieUtil.getCookie("ab_user_id") || "";
        const email = emailID || CookieUtil.getCookie("faul") || "";
        const pincode = CookieUtil.getCookie(LAST_VISITED_PINCODE) || "";

        if (isMobile) {
          const baseStores = serverPageData?.stores ?? [];
          const selectedStore = getInitialMobileStore(baseStores);
          const storeId = selectedStore?.storeId;
          const storeName = selectedStore?.storeName || selectedStore?.name;
          const baseWidgets = selectedStore?.feed?.widgets ?? [];
          if (!storeId || !baseWidgets.length) {
            return;
          }

          const response = await getPageFeedAPI({
            isMobile: true,
            storeName,
            fnpId,
            email,
            pincode,
            campaignWidgetsOnly: true,
          });

          if (cancelled) {
            return;
          }

          const campaignWidgets = extractCampaignWidgetsFromFeedResponse(response, storeId);
          if (campaignWidgets.length > 0) {
            const mergedWidgets = mergeCampaignWidgetsById(baseWidgets, campaignWidgets);
            dispatch(
              setMobileStoreCampaignFeed({
                storeId,
                widgets: mergedWidgets,
                stores: baseStores,
              }),
            );
          }
          return;
        }

        const baseWidgets = serverPageData?.widgets ?? [];
        if (!baseWidgets.length) {
          return;
        }

        const response = await getPageFeedAPI({
          isMobile: false,
          fnpId,
          email,
          pincode,
          campaignWidgetsOnly: true,
        });

        if (cancelled) {
          return;
        }

        const campaignWidgets = extractCampaignWidgetsFromFeedResponse(response);
        if (campaignWidgets.length > 0) {
          const mergedWidgets = mergeCampaignWidgetsById(baseWidgets, campaignWidgets);
          dispatch(setDesktopFeed(mergedWidgets));
        }
      } catch {
        // Loader is cleared by dismissTimer; feed merge is best-effort.
      }
    };

    const hasCampaignBase = isMobile
      ? (serverPageData?.stores?.length ?? 0) > 0
      : (serverPageData?.widgets?.length ?? 0) > 0;

    if (hasCampaignBase) {
      fetchCampaignFeed();
    }

    return () => {
      cancelled = true;
      clearTimeout(dismissTimer);
      setIsCampaignLoading(false);
    };
  }, [isMobile, serverPageData?.widgets]);

  return isCampaignLoading;
};

export default useHomeCampaignFeed;
